import type {
  ParsedEndpoint,
  ParsedHarEntry,
  ParsedHarEntriesSearch,
} from '../ToolCallsRenderers/types';
import type { Header } from 'har-format';
import { z } from 'zod';

const HarHeaderSchema: z.ZodType<Header> = z.object({
  name: z.string(),
  value: z.string(),
  comment: z.string().optional(),
});

const StringLikeSchema = z.union([z.string(), z.number()]).transform(String);

const CountSchema = z.preprocess((value) => {
  if (typeof value === 'string' && value.trim() !== '') {
    return Number(value);
  }

  return value;
}, z.int().nonnegative());

const HarEntryResultSchema = z.object({
  id: StringLikeSchema.optional(),
  method: z.string().optional(),
  url: z.string().optional(),
  authInfo: z.string().optional(),
  responseStatus: StringLikeSchema.optional(),
  responseStatusText: z.string().optional(),
  matchReason: z.string().optional(),
  postData: z.string().optional(),
  bodyPreview: z.string().optional(),
  requestHeaders: HarHeaderSchema.array().optional(),
  responseHeaders: HarHeaderSchema.array().optional(),
  requestHttpVersion: z.string().optional(),
  responseHttpVersion: z.string().optional(),
});

const HarEntriesResultSchema = z.object({
  results: HarEntryResultSchema.array(),
  query: z.string().optional(),
  totalEntries: CountSchema.optional(),
  matchingEntries: CountSchema.optional(),
});

export const parseEndpointFromXML = (xml: string): ParsedEndpoint | null => {
  try {
    const endpointMatch = xml.match(/<endpoint>([\s\S]*?)<\/endpoint>/);

    if (!endpointMatch) return null;

    const endpointContent = endpointMatch[1];

    const idMatch = endpointContent.match(/Endpoint ID:\s*(\S+)/);
    const urlMatch = endpointContent.match(/Endpoint URL:\s*(\S+)/);
    const descMatch =
      endpointContent.match(/Description:\s*([^\n]+)/) ||
      endpointContent.match(/Endpoint Description:\s*([^\n]+)/);
    const methodMatch = endpointContent.match(/Method:\s*(\w+)/);
    const credentialMatch = endpointContent.match(/Credential ID:\s*(\S+)/);
    const pathMatch = endpointContent.match(/Path:\s*([^\n]+)/);
    const rankMatch = endpointContent.match(/Rank:\s*(\d+)/);
    const scoreMatch = endpointContent.match(/Score:\s*([\d.]+)/);

    const rank = rankMatch?.[1] ? parseInt(rankMatch[1], 10) : undefined;

    return {
      endpointId: idMatch?.[1],
      endpointUrl: urlMatch?.[1],
      endpointDescription: descMatch?.[1]?.trim(),
      method: methodMatch?.[1],
      credentialId: credentialMatch?.[1],
      path: pathMatch?.[1]?.trim(),
      rank,
      score: scoreMatch?.[1],
    };
  } catch {
    return null;
  }
};

export const parseEndpointsListFromXML = (
  xml: string,
): ParsedEndpoint[] | null => {
  try {
    const endpointMatches = xml.matchAll(/<endpoint>([\s\S]*?)<\/endpoint>/g);
    const endpoints: ParsedEndpoint[] = [];

    for (const match of endpointMatches) {
      const endpoint = parseEndpointFromXML(match[0]);

      if (endpoint) {
        endpoints.push(endpoint);
      }
    }

    return endpoints.length > 0 ? endpoints : null;
  } catch {
    return null;
  }
};

const PIPE_ENDPOINT_LINE =
  /^(\S+)\s*\|\s*(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD|UNKNOWN)\s+(\S+)(?:\s+-\s+(.*))?$/i;

export const parseEndpointsFromPipeList = (
  content: string,
): ParsedEndpoint[] | null => {
  const endpoints = content
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .flatMap((line) => {
      const match = line.match(PIPE_ENDPOINT_LINE);

      if (!match) {
        return [];
      }

      const method = match[2].toUpperCase();
      const description = match[4]?.trim();

      return [
        {
          endpointId: match[1],
          method: method === 'UNKNOWN' ? undefined : method,
          endpointUrl: match[3],
          endpointDescription:
            description && description !== 'No description available'
              ? description
              : undefined,
        } satisfies ParsedEndpoint,
      ];
    });

  return endpoints.length > 0 ? endpoints : null;
};

/**
 * Parse HAR entries from a structured object (output from har-entries-tool)
 */
export const parseHarEntriesFromObject = (
  content: unknown,
): ParsedHarEntriesSearch | null => {
  const parsed = HarEntriesResultSchema.safeParse(content);

  if (!parsed.success) {
    return null;
  }

  const entries: ParsedHarEntry[] = parsed.data.results.map((result) => ({
    id: result.id ?? '',
    method: result.method,
    url: result.url,
    authInfo: result.authInfo,
    responseStatus: result.responseStatus,
    responseStatusText: result.responseStatusText,
    matchReason: result.matchReason,
    postData: result.postData,
    bodyPreview: result.bodyPreview,
    requestHeaders: result.requestHeaders,
    responseHeaders: result.responseHeaders,
    requestHttpVersion: result.requestHttpVersion,
    responseHttpVersion: result.responseHttpVersion,
  }));

  return {
    entries,
    query: parsed.data.query,
    totalEntries: parsed.data.totalEntries,
    matchingEntries: parsed.data.matchingEntries,
  };
};

export const parseHarEntriesFromMarkdown = (
  markdown: string,
): ParsedHarEntriesSearch | null => {
  try {
    const entries: ParsedHarEntry[] = [];

    // Extract query
    const queryMatch = markdown.match(/\*\*Query:\*\*\s*([^\n]+)/);
    const query = queryMatch?.[1]?.trim();

    // Extract total count
    const totalMatch = markdown.match(
      /\*\*Total HAR Entries Scanned:\*\*\s*(\d+)/,
    );
    const totalEntries = totalMatch?.[1]
      ? parseInt(totalMatch[1], 10)
      : undefined;

    // Extract matching count
    const matchingMatch = markdown.match(
      /\*\*Matching Entries Found:\*\*\s*(\d+)/,
    );
    const matchingEntries = matchingMatch?.[1]
      ? parseInt(matchingMatch[1], 10)
      : undefined;

    // Parse individual entries
    const entryPattern =
      /### HAR Entry:\s*`([^`]+)`[\s\S]*?(?=### HAR Entry:|---|\n\n\*\*Usage:|$)/g;
    const matches = markdown.matchAll(entryPattern);

    for (const match of matches) {
      const entryContent = match[0];
      const id = match[1];

      const methodMatch = entryContent.match(/\*\*Method:\*\*\s*(\w+)/);
      const urlMatch = entryContent.match(/\*\*URL:\*\*\s*([^\n]+)/);
      const authMatch = entryContent.match(
        /\*\*Authentication:\*\*\s*([^\n]+)/,
      );
      const statusMatch = entryContent.match(
        /\*\*Response Status:\*\*\s*([^\n]+)/,
      );
      const reasonMatch = entryContent.match(
        /\*\*Match Reason:\*\*\s*([^\n]+)/,
      );

      entries.push({
        id,
        method: methodMatch?.[1],
        url: urlMatch?.[1]?.trim(),
        authInfo: authMatch?.[1]?.trim(),
        responseStatus: statusMatch?.[1]?.trim(),
        matchReason: reasonMatch?.[1]?.trim(),
      });
    }

    if (entries.length > 0 || totalEntries !== undefined) {
      return {
        entries,
        query,
        totalEntries,
        matchingEntries,
      };
    }

    return null;
  } catch {
    return null;
  }
};
